/**
 * useTutorLiveFeed — le fil de la séance du tuteur, la session avec le champion, la génération (la colonne de
 * droite de `<TutorLivePage>`).
 *
 * Ce qu'il lit (tech-derisk, point 4) : les tranches Redux qu'écrit l'écouteur global `LKEvents`
 * (`liveMode.raisedHands`, `outOfAppIds`, `timesSinceLastCall`, `soundModeIds`, `ongoingCheckpointIds`,
 * `liveParticipants`, `recentFinalizedActivities`, `autoModeStartedActivities`, `completedConversations`) et la
 * mémoire de l'ancien panneau (`data.tutorEventsPanel` : la croix, l'instant de chaque signal, l'envoi du
 * champion), jamais le label `events-panel`. Ce qu'il garde (point 8) : la vie de chaque carte dans
 * `data.tutorLiveSession`, rangée par compte et par séance.
 *
 * Ce qu'il rend :
 *  - `zone` : un seul fil, filtré sur l'élève regardé (`selection.selectedId`), le filtre retirable (la puce
 *    « Tous » et le prénom du titre, `onToggleFilter`) ; le champ qui génère, sa cible suit le filtre ;
 *  - `classSession` : le fil sans filtre, que l'écran montre en mosaïque (jamais filtrée) ;
 *  - `layer` : l'accusé d'un geste (snackbar), et la feuille du moment (le formulaire, le recadrage, l'aperçu) ;
 *  - `samSummary` : la feuille des résumés du champion (`checkpoint-rh`) ;
 *  - `generate(studentId?, from?)` : « Ajouter une activité » de la pellicule et « Générer à partir de l'exo » du
 *    centre ;
 *  - `pending` : ce qui attend une décision chez chaque élève, que l'écran pose sur `students.list[].pending`
 *    (la cloche et la vignette).
 *
 * La popup de lancement du champion (`TutorCheckpointManager`, `checkpoint-rh`) n'a pas de port : en prod,
 * toute salle `events-panel` la fait taire (`skipCheckpoint`, `LiveParticipant.js:277`), et l'écran ne
 * s'ouvre que là. « Envoyer » le champion reste la carte du fil.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"

import { syncTutorEventsStartedAt } from "../../../../../../data/redux/reducers/data/tutorEventsPanel"
import {
    appendTutorLiveSession,
    pruneTutorLiveSession,
    seenTutorLiveSession,
    syncTutorLiveSession,
    tutorLiveSessionKey,
} from "../../../../../../data/redux/reducers/data/tutorLiveSession"
import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { hasFeature } from "../../../../../../data/utility/Utils"
import {
    type TutorLiveFeedZone,
    type TutorLiveOverlaysZone,
} from "../../../../../../design-system/pages/TutorLivePage/TutorLivePage"
import { apiCall } from "../../../../../../engine/shared/apiCall"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { track } from "../../../hooks/trackEvent"
import { type UITutorLiveStudent } from "../../../types/tutorLive"
import { selectAccount, selectAccountStudents } from "../../../../view-models/shared/displayedStudent"
import {
    activityChapter,
    activityReviewStatus,
    clockOf,
    enrolledStudents,
    helperLines,
    type ApiLiveSessionUser,
    type ApiStudentLists,
} from "../../../../adapters/tutor/Live/tutorLiveFeedAdapter"
import {
    dialogueWithStudentId,
    signalStartTimes,
    tutorLiveSignals,
} from "../../../../view-models/tutor/Live/tutorLiveFeedRules"
import {
    tutorLiveFeedItems,
    tutorLiveFeedNextWake,
    tutorLiveFeedSync,
    tutorLiveFeedView,
    tutorLiveTimelineChanges,
    tutorSessionRibbon,
    isoOf,
    type TutorLiveCompletedConversation,
    type TutorLiveFeedRecord,
    type TutorLiveTransientEntry,
} from "../../../../view-models/tutor/Live/tutorLiveFeedViewModel"
import { rawId, type TutorLiveLayer, type TutorLiveRoomContext, type TutorLiveSelection } from "./tutorLiveContract"
import { type TutorLiveGenerateSource } from "./useTutorLiveCenterActions"
import { type TutorLiveGenerateRequest } from "./useTutorLiveChromeChat"
import { type TutorLiveFeedGenerationRequest } from "./useTutorLiveFeedGeneration"
import { useTutorLiveFeedActions } from "./useTutorLiveFeedActions"
import { useTutorLiveFeedGeneration } from "./useTutorLiveFeedGeneration"
import { useTutorLiveFeedProposals } from "./useTutorLiveFeedProposals"
import { useTutorLiveFeedSamSummary } from "./useTutorLiveFeedSamSummary"
import { useTutorLiveFeedSheet } from "./useTutorLiveFeedSheet"

/** La mémoire née plus tard que le début du live : elle a commencé ailleurs, la colonne le dit (point 8). */
const STARTED_ELSEWHERE_MS = 2 * 60 * 1000

/** Les mesures de l'ancienne popup du résumé (`ConversationSummaryPopup`, F-100) : son affichage, sa croix. */
export const SUMMARY_POPUP_EVENTS = {
    display: { name: "checkpoint.summarypopup.display" },
    close: { name: "checkpoint.summarypopup.close" },
} as const

/** Une demande de génération, telle que les trois appelants la font. */
export type TutorLiveGenerateCall = {
    /** Les élèves visés ; vide : tout le groupe. */
    studentIds: string[]
    from?: TutorLiveGenerateSource
    attachment?: TutorLiveGenerateRequest["attachment"]
    /** D'où vient la pièce : une photo du chat (l'heure de son message), sinon le trombone. */
    origin?: TutorLiveFeedGenerationRequest["origin"]
}

/** La source d'une semblable, telle que la génération la prend (« Générer à partir de l'exo », « /similaire »). */
function generationFrom(source: TutorLiveGenerateSource): NonNullable<TutorLiveFeedGenerationRequest["from"]> {
    return {
        materialId: source.materialId,
        title: source.title,
        level: source.level,
        confidence: source.confidence,
        subjectId: source.subjectId,
        className: source.className,
        preview: source.preview,
    }
}

/**
 * Le chapitre de l'activité qu'un élève a devant lui (`onScreen`), lu dans ses listes : la photo du chat le reprend.
 * Aucun : `null`.
 */
function chapterOnScreen(
    student: UITutorLiveStudent | undefined,
    accountStudents: (ApiStudentLists & { id?: string | number })[]
): TutorLiveFeedGenerationRequest["chapter"] | null {
    const activityId = student?.activities.find((activity) => activity.onScreen === true)?.id
    if (student == null || activityId == null) return null
    return activityChapter(
        accountStudents.find((candidate) => String(candidate?.id) === student.id),
        activityId
    )
}

export type TutorLiveFeed = {
    zone: TutorLiveFeedZone
    layer: TutorLiveLayer
    samSummary: TutorLiveOverlaysZone["samSummary"]
    /**
     * Générer, pour les trois appelants de l'écran : la pellicule (`{ studentIds: [élève] }`), le centre
     * (`{ studentIds: [élève], from }`, « Générer à partir de cet exo »), la barre et le chat
     * (`{ studentIds, attachment }`, vide : tout le groupe).
     */
    generate: (request: TutorLiveGenerateCall) => void
    /** Le fil de tout le groupe : l'écran le met dans `zone.session` en mosaïque, jamais filtrée. */
    classSession: TutorLiveFeedZone["session"]
    /** Ce qui attend une décision chez chaque élève : `students.list[].pending`. */
    pending: Record<string, number>
    /**
     * La carte en vol (F-62) : l'écran la pose sur `TutorLiveFeedZone.pending`, à part de la zone (elle change
     * sans que le fil change).
     */
    feedPending: { itemId: string; actionId: string } | null
    /**
     * « Actualiser » (F-12, le `sync-alt` de `NewStudentsListItem`) : relit la séance, l'appel, et les
     * évaluations à venir de l'élève regardé. Le fil n'a pas de place pour le bouton : il vit dans « Plus ».
     */
    refresh: { onRefresh: () => void; refreshing: boolean }
}

type Ids = (string | number)[] | null | undefined
const strings = (ids: Ids): string[] => (ids ?? []).map(String)
const byString = <T>(map: Record<string, T> | unknown[] | null | undefined): Record<string, T> =>
    map != null && !Array.isArray(map) ? (map as Record<string, T>) : {}

type CompletedRaw = {
    liveSessionId?: unknown
    studentId?: string | number
    read?: boolean
    timestamp?: number
    conversation?: {
        id?: string | number
        summary?: string
        summary_title?: string
        status?: string
        trigger_context?: string
    }
}

/** Les tranches lues, en une passe (`shallowEqual` : une tranche qui ne bouge pas ne relance rien). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function selectFeedSlices(state: any) {
    const liveMode = state?.liveMode ?? {}
    const panel = state?.data?.tutorEventsPanel ?? {}
    return {
        raisedHands: liveMode.raisedHands as Ids,
        outOfAppIds: liveMode.outOfAppIds as Ids,
        timesSinceLastCall: liveMode.timesSinceLastCall as Record<string, number> | undefined,
        soundModeIds: liveMode.soundModeIds as Ids,
        ongoingCheckpointIds: liveMode.ongoingCheckpointIds as Ids,
        liveParticipants: liveMode.liveParticipants as Ids,
        recentFinalizedActivities: liveMode.recentFinalizedActivities,
        autoModeStartedActivities: liveMode.autoModeStartedActivities,
        completedConversations: liveMode.completedConversations as CompletedRaw[] | undefined,
        dismissed: panel.dismissedEventHashes as string[] | undefined,
        startedAtByKey: panel.startedAtByKey as Record<string, number> | undefined,
        samSentAtByKey: panel.samSentAtByKey as Record<string, number> | undefined,
        sessions: state?.data?.tutorLiveSession?.sessions as
            | Record<
                  string,
                  {
                      createdAt?: number
                      entries?: TutorLiveFeedRecord[]
                      timeline?: TutorLiveFeedRecord[]
                      seen?: Record<string, number>
                  }
              >
            | undefined,
        account: selectAccount(state),
        accountStudents: selectAccountStudents(state) as (ApiStudentLists & { id?: string | number })[],
    }
}

export function useTutorLiveFeed(
    room: TutorLiveRoomContext,
    selection: TutorLiveSelection,
    students: UITutorLiveStudent[],
    /**
     * `mosaic` : la mosaïque est montrée ; son fil n'est jamais filtré, et le champ vise tout le groupe.
     * `presenting` : une présentation part ou dure ; la relecture ne s'ouvre alors que d'un geste.
     * `chatOpen` : le chat est ouvert ; une image glissée ou collée lui reste. `centerOnly` : tout l'écran est partagé.
     * `similarSource` : l'activité ouverte au centre pour un élève (« /similaire »).
     */
    options: {
        mosaic?: boolean
        presenting?: boolean
        chatOpen?: boolean
        centerOnly?: boolean
        similarSource?: (studentId: string) => TutorLiveGenerateSource | null
    } = {}
): TutorLiveFeed {
    const dispatch = useDispatch()
    const slices = useSelector(selectFeedSlices, shallowEqual)
    const liveSessionId = room.liveSessionId
    const sessionId = liveSessionId != null ? String(liveSessionId) : ""
    const accountId = slices.account?.id != null ? String(slices.account.id) : room.me.id
    const memoryKey = tutorLiveSessionKey(accountId, liveSessionId) as string | null
    const memory = memoryKey != null ? slices.sessions?.[memoryKey] : undefined
    const records = useMemo(() => memory?.entries ?? [], [memory?.entries])
    // Le fil et la chronologie (le mode du son, l'activité devant chaque élève), gardés à part : le ruban et les
    // activités du fil les lisent ensemble.
    const withTimeline = useMemo(
        () => (memory?.timeline != null && memory.timeline.length > 0 ? [...records, ...memory.timeline] : records),
        [records, memory?.timeline]
    )

    // La séance : ses inscrits (`liveSession.users`), ses labels.
    const liveSession = useMemo(
        () =>
            (
                (slices.account?.live_sessions ?? []) as {
                    id?: unknown
                    users?: ApiLiveSessionUser[]
                    live_room?: unknown
                }[]
            ).find((session) => String(session?.id) === sessionId),
        [slices.account?.live_sessions, sessionId]
    )
    const users = useMemo(() => liveSession?.users ?? [], [liveSession?.users])
    const checkpointRh = hasFeature(liveSession?.live_room, "checkpoint-rh") as boolean
    const quizAvailable = hasFeature(liveSession?.live_room, "prerequisite-quiz") as boolean
    const studentIds = useMemo(() => {
        const enrolled = enrolledStudents(users).map((user) => String(user.id))
        return enrolled.length > 0 ? enrolled : students.map((student) => student.id)
    }, [users, students])
    const names = useMemo(
        () =>
            studentIds.map((id) => {
                const shown = students.find((student) => student.id === id)
                const user = users.find((u) => String(u.id) === id)
                return {
                    id,
                    firstname: shown?.firstname ?? user?.firstname ?? user?.display_name ?? "L'élève",
                    avatarUrl: shown?.avatarUrl,
                }
            }),
        [studentIds, students, users]
    )
    const namesRef = useRef(names)
    namesRef.current = names
    const firstnameOf = useCallback(
        (id: string) => namesRef.current.find((name) => name.id === id)?.firstname ?? "L'élève",
        []
    )

    // ── L'horloge et l'anti-rebond de la sortie de l'appli. L'heure est lue à chaque rendu ; l'ancien `tick`
    // (1 Hz) redessinait toute la salle, vidéos comprises : ici un seul minuteur, posé sur la prochaine
    // échéance du fil (`tutorLiveFeedNextWake`), réveille l'écran — et aucun quand rien n'attend.
    const [, wake] = useReducer((count: number) => count + 1, 0)
    const now = Date.now()
    const outSinceRef = useRef<Record<string, number>>({})
    const outOfApp = strings(slices.outOfAppIds)
    const outKey = outOfApp.join(",")
    // Le premier instant vu hors de l'appli, par élève (l'ancien `outOfAppDebounceRef`), tenu au rendu : les
    // signaux le lisent dans le même rendu.
    useMemo(() => {
        const current = new Set(outKey === "" ? [] : outKey.split(","))
        const next: Record<string, number> = {}
        current.forEach((id) => (next[id] = outSinceRef.current[id] ?? Date.now()))
        outSinceRef.current = next
    }, [outKey])

    const present = strings(slices.liveParticipants)
    const soundModeIds = strings(slices.soundModeIds)
    const raisedHands = strings(slices.raisedHands)
    const ongoing = strings(slices.ongoingCheckpointIds)
    const signals = tutorLiveSignals({
        studentIds,
        present,
        raisedHands,
        outOfAppSince: outSinceRef.current,
        timesSinceLastCall: byString(slices.timesSinceLastCall),
        soundModeIds,
        ongoingCheckpointIds: ongoing,
        recentFinalizedActivities: byString(slices.recentFinalizedActivities),
        autoModeStartedActivities: byString(slices.autoModeStartedActivities),
        now,
    })
    const times = signalStartTimes(signals, slices.startedAtByKey, now)

    // Les instants persistés (`syncTutorEventsStartedAt`), écrits au changement seulement.
    const additionsKey = Object.keys(times.additions).join("|")
    const removalsKey = times.removals.join("|")
    const timesRef = useRef(times)
    timesRef.current = times
    useEffect(() => {
        const { additions, removals } = timesRef.current
        if (additionsKey === "" && removalsKey === "") return
        dispatch(
            syncTutorEventsStartedAt({
                additions: additionsKey !== "" ? additions : undefined,
                removals: removalsKey !== "" ? removals : undefined,
            }) as never
        )
    }, [dispatch, additionsKey, removalsKey])

    // Les résumés du champion qui attendent d'être lus (l'ancienne popup, sous `checkpoint-rh` seulement).
    const completed = useMemo<TutorLiveCompletedConversation[]>(
        () =>
            !checkpointRh
                ? []
                : (slices.completedConversations ?? [])
                      .filter(
                          (entry) =>
                              entry?.read !== true &&
                              String(entry?.liveSessionId) === sessionId &&
                              entry?.conversation?.id != null &&
                              entry.studentId != null
                      )
                      .map((entry) => ({
                          studentId: String(entry.studentId),
                          conversationId: String(entry.conversation?.id),
                          at: entry.timestamp ?? Date.now(),
                          title: entry.conversation?.summary_title ?? undefined,
                          summary: entry.conversation?.summary ?? "",
                          needsHelp: entry.conversation?.status === "tutor_needed",
                          quizPassed:
                              entry.conversation?.trigger_context === "prerequisite_quiz" &&
                              entry.conversation?.status === "ended",
                      })),
        [checkpointRh, slices.completedConversations, sessionId]
    )

    const dialogueWith = dialogueWithStudentId(soundModeIds, studentIds, present)
    const cardsInput = {
        signals,
        startedAt: times.startedAt,
        samSentAtByKey: slices.samSentAtByKey,
        dismissed: slices.dismissed ?? [],
        completed,
        students: names,
        reviewStatus: (studentId, activityId) =>
            activityReviewStatus(
                slices.accountStudents.find((student) => String(student?.id) === studentId),
                activityId
            ),
        dialogueWith,
        isWeb: IS_WEB,
        helperName: room.helperName,
        now,
    }
    const { cards, traces } = tutorLiveFeedItems(cardsInput)

    // F-100 : la mesure de l'ancienne popup du résumé, une fois par conversation.
    const displayedRef = useRef(new Set<string>())
    useEffect(() => {
        for (const card of cards) {
            if (card.signal !== "sam_summary" || displayedRef.current.has(card.id)) continue
            displayedRef.current.add(card.id)
            track(SUMMARY_POPUP_EVENTS.display)
        }
    })

    // ── La mémoire : la séance de la clé seule, les cartes nées et tombées.
    useEffect(() => {
        if (memoryKey != null) dispatch(pruneTutorLiveSession(memoryKey) as never)
    }, [dispatch, memoryKey])
    const sync = tutorLiveFeedSync(records, cards, { students: names, helperName: room.helperName, now }, traces)
    const syncKey = `${sync.opened.map((r) => r.id).join(",")}|${sync.closed.map((c) => c.id).join(",")}`
    const syncRef = useRef(sync)
    syncRef.current = sync
    useEffect(() => {
        if (memoryKey == null || syncKey === "|") return
        dispatch(syncTutorLiveSession(memoryKey, syncRef.current) as never)
    }, [dispatch, memoryKey, syncKey])

    // Le mode du son et l'activité devant chaque élève, gardés à chaque changement : le ruban de la séance et les
    // traces rangées sous leur activité s'en déduisent (V1 : ce que ce navigateur a vu depuis l'ouverture de la salle).
    const timeline = tutorLiveTimelineChanges(
        withTimeline,
        students.map((student) => ({
            studentId: student.id,
            mode: student.soundMode,
            focusId: student.activities.find((activity) => activity.onScreen === true)?.id ?? null,
        })),
        now
    )
    const timelineKey = timeline.map((change) => `${change.kind}:${change.studentId}:${change.value}`).join("|")
    const timelineRef = useRef(timeline)
    timelineRef.current = timeline
    useEffect(() => {
        if (memoryKey == null || timelineKey === "") return
        for (const change of timelineRef.current) dispatch(appendTutorLiveSession(memoryKey, change) as never)
    }, [dispatch, memoryKey, timelineKey])

    // Le dernier passage sur un élève : quand on le quitte, le repère « Depuis ton dernier passage ».
    const previousSelected = useRef(selection.selectedId)
    useEffect(() => {
        const left = previousSelected.current
        previousSelected.current = selection.selectedId
        if (left != null && left !== selection.selectedId && memoryKey != null)
            dispatch(seenTutorLiveSession(memoryKey, left) as never)
    }, [dispatch, memoryKey, selection.selectedId])

    // ── Le filtre : retiré à la demande, remis à chaque nouvel élève regardé.
    const [filterOff, setFilterOff] = useState(false)
    useEffect(() => setFilterOff(false), [selection.selectedId])
    // En mosaïque, le fil est celui de tout le groupe (l'écran le pose) : le champ vise tout le groupe aussi. Il
    // disait « pour Thérèse P. » sous « Tous » (marche du 03/11).
    const targetId = selection.selectedId != null && !filterOff && options.mosaic !== true ? selection.selectedId : null

    // ── Les gestes, le résumé, la génération.
    const [preview, setPreview] = useState<{ studentId: string; uri: string } | null>(null)
    const sam = useTutorLiveFeedSamSummary({
        liveSessionId,
        users,
        firstnameOf,
        enabled: checkpointRh,
        onOpenPhoto: (studentId, uri) => setPreview({ studentId, uri }),
    })
    const send = room.live?.send ?? null
    const actions = useTutorLiveFeedActions({
        liveSessionId,
        meId: room.me.id,
        send,
        selection,
        studentIds,
        present,
        soundModeIds,
        firstnameOf,
        isWeb: IS_WEB,
        memoryKey,
        openSummary: sam.open,
    })
    // « <champion> propose » est appelé après la génération : elle lit son envoi en cours par une ref, au geste.
    const proposalsRef = useRef<{ sending: boolean } | null>(null)
    const similarSourceRef = useRef(options.similarSource)
    similarSourceRef.current = options.similarSource
    const similarSource = useCallback((studentId: string) => {
        const source = similarSourceRef.current?.(studentId)
        return source != null ? generationFrom(source) : null
    }, [])
    const generation = useTutorLiveFeedGeneration({
        targetId,
        studentIds,
        firstnameOf,
        liveSessionId,
        quizAvailable,
        send,
        memoryKey,
        helperName: room.helperName,
        notify: actions.notify,
        watching: options.mosaic === true ? null : (selection.selectedId ?? null),
        presenting: options.presenting === true,
        similarSource,
        // Ni pendant une présentation : la zone de dépôt couvrirait ce que voient les élèves.
        roomDrop: options.chatOpen !== true && options.centerOnly !== true && options.presenting !== true,
        isCreateLocked: () => proposalsRef.current?.sending === true,
    })

    // « <champion> propose » : une activité faite d'avance sur un rendu qui attend (bornée, comptée).
    const proposals = useTutorLiveFeedProposals({
        cards,
        accountStudents: slices.accountStudents,
        firstnameOf,
        send,
        telemetry: room.telemetry,
        tutorGenerating: generation.busy,
        tutorCreating: generation.creating,
        presenting: options.presenting === true,
        quizAvailable,
        notify: actions.notify,
        helperName: room.helperName,
    })

    proposalsRef.current = proposals

    const transient = useMemo<TutorLiveTransientEntry[]>(
        () => [
            ...helperLines(users, sessionId).map((line) => ({
                studentId: line.studentId,
                at: line.at,
                entry: {
                    kind: "helper" as const,
                    id: line.id,
                    at: isoOf(line.at),
                    title: line.title,
                    text: line.text,
                },
            })),
            ...proposals.transient,
            ...generation.transient,
        ],
        [users, sessionId, generation.transient, proposals.transient]
    )

    const startedHereAt =
        memory?.createdAt != null && memory.createdAt - room.startedAt > STARTED_ELSEWHERE_MS
            ? memory.createdAt
            : undefined
    const view = tutorLiveFeedView({
        cards,
        records: withTimeline,
        transient,
        students: names,
        selectedId: selection.selectedId,
        filterOff,
        withHelper: ongoing,
        seen: memory?.seen ?? {},
        startedHereAt,
        now,
        activitiesOf: (studentId) => students.find((student) => student.id === studentId)?.activities ?? [],
    })
    // Le ruban de l'élève regardé, depuis l'ouverture de la salle sur ce navigateur.
    const ribbonFor = options.mosaic === true ? undefined : selection.selectedId
    const ribbon =
        ribbonFor != null
            ? tutorSessionRibbon({
                  records: withTimeline,
                  studentId: ribbonFor,
                  openedAt: memory?.createdAt ?? room.startedAt,
                  now,
                  activities: students.find((student) => student.id === ribbonFor)?.activities ?? [],
                  clock: clockOf,
              })
            : null

    // La même référence tant que les comptes ne changent pas : la pellicule les lit à chaque rendu (`pendingOf`).
    const pendingKey = JSON.stringify(view.pending)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pending = useMemo(() => view.pending, [pendingKey])

    const nextWake = tutorLiveFeedNextWake({
        cards,
        records,
        outOfAppSince: outSinceRef.current,
        samSentAtByKey: slices.samSentAtByKey,
        now,
    })
    useEffect(() => {
        if (nextWake == null) return
        const timer = setTimeout(wake, Math.max(0, nextWake - Date.now()))
        return () => clearTimeout(timer)
    }, [nextWake])

    const cardsRef = useRef(cards)
    cardsRef.current = cards
    const onAction = useCallback(
        (itemId: string, actionId: string) => {
            const card = cardsRef.current.find((c) => c.id === itemId)
            if (card != null) actions.run(card, actionId)
        },
        [actions]
    )
    const onDismiss = useCallback(
        (itemId: string) => {
            const card = cardsRef.current.find((c) => c.id === itemId)
            if (card == null) return
            if (card.signal === "sam_summary") track(SUMMARY_POPUP_EVENTS.close)
            actions.dismiss(card)
        },
        [actions]
    )

    const [open, setOpen] = useState(false)
    // La relecture s'ouvre au centre : le tiroir de la session (sous 1 024 px) se range, il la couvrirait.
    const review = proposals.review ?? generation.review
    const reviewOpen = review?.open === true
    useEffect(() => {
        if (reviewOpen) setOpen(false)
    }, [reviewOpen])
    const toggleFilter = useCallback(() => setFilterOff((value) => !value), [])

    const generationRef = useRef(generation)
    generationRef.current = generation
    const selectionRef = useRef(selection)
    selectionRef.current = selection

    // « Actualiser » : les trois lectures du legacy, désactivé tant qu'elles volent (`request`).
    const [refreshing, setRefreshing] = useState(false)
    const refreshingRef = useRef(false)
    const onRefresh = useCallback(() => {
        if (refreshingRef.current || liveSessionId == null) return
        refreshingRef.current = true
        setRefreshing(true)
        const studentId = selectionRef.current.selectedId
        const reads: Promise<unknown>[] = [
            apiCall(dispatch as never, liveServiceV2.getLiveSessionV2, { routeParams: { id: liveSessionId } }),
            apiCall(dispatch as never, liveServiceV2.getCallV2, { routeParams: { id: liveSessionId } }),
        ]
        if (studentId != null)
            reads.push(
                apiCall(dispatch as never, schoolAssessmentsService.getSchoolAssessments, {
                    urlParams: { student_id: rawId(studentId), upcoming: true },
                })
            )
        void Promise.allSettled(reads).then(() => {
            refreshingRef.current = false
            setRefreshing(false)
        })
    }, [dispatch, liveSessionId])

    const generate = useCallback((request: TutorLiveGenerateCall) => {
        // La cible suit le filtre : un élève nommé est regardé et filtré ; aucun, tout le groupe (le filtre
        // retiré si quelqu'un est regardé) ; plusieurs, ces élèves-là, ensemble (une activité commune).
        const [only] = request.studentIds
        const single = request.studentIds.length === 1
        if (single) {
            selectionRef.current.select(only)
            setFilterOff(false)
        } else setFilterOff(true)
        setOpen(true)
        const attachment = request.attachment
        // Une photo du chat reprend le chapitre de l'activité que l'élève a devant lui.
        const chapter =
            attachment != null && request.origin?.kind === "chat" && single
                ? chapterOnScreen(
                      studentsRef.current.find((student) => student.id === only),
                      accountStudentsRef.current
                  )
                : undefined
        generationRef.current.request({
            target: single ? only : null,
            ids: request.studentIds.length > 1 ? request.studentIds : undefined,
            from: request.from != null ? generationFrom(request.from) : undefined,
            attachment:
                attachment != null
                    ? { uri: attachment.uri, name: attachment.name, mimeType: attachment.mimeType }
                    : undefined,
            origin: request.origin,
            chapter: chapter ?? undefined,
        })
    }, [])
    const studentsRef = useRef(students)
    studentsRef.current = students
    const accountStudentsRef = useRef(slices.accountStudents)
    accountStudentsRef.current = slices.accountStudents

    const { sheet, editor } = useTutorLiveFeedSheet({
        generation,
        preview:
            preview != null
                ? {
                      uri: preview.uri,
                      onClose: () => setPreview(null),
                      onCreateActivity: () => {
                          const { studentId, uri } = preview
                          setPreview(null)
                          selection.select(studentId)
                          setFilterOff(false)
                          const pdf = /\.pdf($|\?)/i.test(uri)
                          generation.attach(
                              {
                                  uri,
                                  name: `checkpoint_${Date.now()}`,
                                  mimeType: pdf ? "application/pdf" : "image/png",
                              },
                              studentId
                          )
                      },
                  }
                : null,
    })

    // Les gestes des lignes de la génération : la carte « prêtes », « <champion> propose ».
    const onReady = generation.onReady
    const onProposal = proposals.onAction
    const onGeneration = useCallback(
        (entryId: string, action: "review" | "sendFirst" | "see" | "send" | "dismiss") => {
            if (action === "review" || action === "sendFirst") onReady(action)
            else onProposal(entryId, action)
        },
        [onReady, onProposal]
    )

    const zone: TutorLiveFeedZone = {
        items: view.items,
        onAction,
        onDismiss,
        open,
        onToggle: () => setOpen((value) => !value),
        session: view.session,
        onToggleFilter: view.filter != null ? toggleFilter : undefined,
        onResult: generation.onResult,
        // La puce dépliée se pose dans le champ, sur place (jamais une modale par-dessus la salle).
        composer:
            generation.composer != null && editor != null ? { ...generation.composer, editor } : generation.composer,
        onGeneration,
        onPeek: generation.onPeek,
        review,
        peek: generation.peek,
        ribbon,
        drop: generation.dropping
            ? {
                  firstname: targetId != null ? firstnameOf(targetId) : null,
                  avatarUrl:
                      targetId != null ? students.find((student) => student.id === targetId)?.avatarUrl : undefined,
              }
            : null,
    }

    // Ni `snackbarAutomatic` ni `dialogAutomatic` : chaque surface du fil naît d'un geste du masteur (l'accusé
    // d'une carte ou d'une création, le recadrage d'un collage, l'aperçu d'une photo), et le
    // résumé du champion ne s'ouvre que sur « Lire le résumé ». Les signaux, eux, vivent dans la colonne, que
    // la page en « centre seul » ne montre pas.
    return {
        zone,
        classSession: view.classSession,
        layer: { snackbar: actions.snackbar, sheet },
        samSummary: sam.props,
        generate,
        pending,
        feedPending: actions.pending,
        refresh: { onRefresh, refreshing },
    }
}
